import { Badge, Card, LuIcon, Separator, Text } from "@overmap-ai/blocks"
import { type ProcedureStepFieldValuesReview, type Stored } from "@overmap-ai/common"
import { selectUserById } from "@overmap-ai/core"
import { EditorContent, useEditor } from "@tiptap/react"
import { memo } from "react"

import { procedureStepFieldValuesReviewTypeData } from "../../mappings/procedures"
import { useAppSelector } from "../../store"
import { BASE_EXTENSIONS, LINK_NODES, MENTION_EXTENSIONS, useEditorContent } from "../../tiptap"
import { TextBubbleMenu } from "../../tiptap/components"
import { EditorContext } from "../../tiptap/context"
import { formatCompactDate } from "../../utils/date"
import { getUserDisplayLabel } from "../../utils/users"

interface ProcedureStepReviewActivityTimelineItemProps {
	procedureStepReview: Stored<ProcedureStepFieldValuesReview>
}

export const ProcedureStepReviewActivityTimelineItem = memo<ProcedureStepReviewActivityTimelineItemProps>((props) => {
	const { procedureStepReview } = props

	const createdBy = useAppSelector(selectUserById(procedureStepReview.created_by ?? -1))
	const editorContent = useEditorContent(procedureStepReview.content)

	const editor = useEditor(
		{
			editorProps: {
				attributes: {
					class: "outline-none",
				},
			},
			content: editorContent,
			editable: false,
			extensions: [...BASE_EXTENSIONS, ...LINK_NODES, ...MENTION_EXTENSIONS],
		},
		[],
	)

	const { icon, actionLabel, accentColor } = procedureStepFieldValuesReviewTypeData[procedureStepReview.type]

	return (
		<div className="flex h-max w-full gap-4">
			<div className="flex flex-col items-center gap-2">
				<Badge icon accentColor={accentColor} radius="full" variant="soft" size="xs">
					<LuIcon icon={icon} />
				</Badge>
				<Separator orientation="vertical" className="grow" />
			</div>

			<Card size="xs" className="p-0! flex grow flex-col" accentColor="base">
				<div className="flex items-center gap-2 px-4 py-2">
					<Text size="xs">{getUserDisplayLabel(createdBy)}</Text>
					<Text accentColor="base" size="xs">
						{actionLabel}&nbsp;&#8226;&nbsp;
						{formatCompactDate(procedureStepReview.submitted_at)}
					</Text>
				</div>
				<Separator size="full" accentColor="base" />
				<EditorContext value={editor}>
					<EditorContent editor={editor} className="h-max grow p-4 text-xs" />
					<TextBubbleMenu editor={editor} />
				</EditorContext>
			</Card>
		</div>
	)
})
ProcedureStepReviewActivityTimelineItem.displayName = "ProcedureStepReviewActivityTimelineItem"
